//
// Copyright (c) 2024 Comcast
//

import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { confirmable, createConfirmation } from 'react-confirm'

import colors from 'components/utils/colors'

import Modal from 'components/Shared/Modal/Index'
import Button from 'components/Shared/Button/Index'
import ModalHeader from 'components/Shared/Modal/ModalHeader'
import ModalBody from 'components/Shared/Modal/ModalBody'
import ModalFooter from 'components/Shared/Modal/ModalFooter'
import RequiredLegend from 'components/Shared/TableForm/RequiredLegend'
import JustificationInput from './JustificationInput'

let dismissOrig = null

export const dismissConfirmation = () => {
  dismissOrig?.()
}

const FooterInfoline = styled.div`
  position: absolute;
  top: 16px;
  left: 32px;
  margin: 0 !important;
`

const Message = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 16px;
  color: ${colors.contentSecondaryActive};
`

const ConfirmDialog = ({
  show, proceed, cancel, dismiss, confirmLabel, cancelLabel, title, message,
  bodyComponent: BodyComponent, bodyComponentProps, showJustification, noMargins,
}) => {
  const confirmBtn = useRef()
  const [justificationValue, setJustificationValue] = useState('')

  useEffect(() => {
    dismissOrig = dismiss

    return () => {
      dismissOrig = null
    }
  }, [dismiss])

  const focusOnBtn = () => {
    confirmBtn.current?.focus()
  }

  const onJustificationChange = (e) => {
    setJustificationValue(e.target.value)
  }

  const onConfirm = (e) => {
    if (showJustification) {
      proceed({ justification: justificationValue })
      return
    }

    proceed()
  }

  let body = <Message>{message}</Message>
  if (BodyComponent) {
    body = <BodyComponent additionalProps={bodyComponentProps} />
  }

  const isConfirmDisabled = Boolean(showJustification && !justificationValue.trim())

  return (
    <div>
      <Modal open={show} closeModal={dismiss} onAfterOpen={focusOnBtn}>
        <ModalHeader closeModal={dismiss}>{title}</ModalHeader>
        <ModalBody
          noMargins={noMargins || showJustification}
        >
          {body}
          <JustificationInput
            value={justificationValue}
            onChange={onJustificationChange}
            showJustification={showJustification}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            large
            ref={confirmBtn}
            disabled={isConfirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
          <Button large secondary onClick={cancel}>{cancelLabel}</Button>
          <FooterInfoline>
            <RequiredLegend isVisible={showJustification} />
          </FooterInfoline>
        </ModalFooter>
      </Modal>
    </div>
  )
}

ConfirmDialog.propTypes = {
  show: PropTypes.bool,
  proceed: PropTypes.func,
  cancel: PropTypes.func,
  dismiss: PropTypes.func,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  title: PropTypes.string,
  message: PropTypes.string,
  bodyComponent: PropTypes.func,
  bodyComponentProps: PropTypes.object,
  showJustification: PropTypes.bool,
  noMargins: PropTypes.bool,
}

export function confirm (options_) {
  const defaultOptions = {
    title: 'title',
    message: 'message',
    confirmLabel: options_.t ? options_.t('common.yes') : 'Yes',
    cancelLabel: options_.t ? options_.t('common.no') : 'No',
  }
  const options = { ...defaultOptions, ...options_ }

  return createConfirmation(confirmable(ConfirmDialog))(options)
}

export default ConfirmDialog
